// Media API — typed wrappers for /api/v1/media/* (W2 §6.3 cross-module).
//
// Per W2 + architecture §3: media is uploaded as multipart/form-data,
// stored at ``./uploads/diary/{YYYY}/{MM}/{uuid}.{ext}``, and reused
// across diary entries via the attach-to-diary flow.

import { api as openapi } from "./client";
import { ApiError } from "./pair";
import { assertResponseBody } from "./emptyBody";
import { API_REQUEST_FAILED } from "../i18n/errors";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

interface CallOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /** openapi-fetch reads query params from `params.query` — see diary.ts. */
  params?: { query: Record<string, unknown> };
  /**
   * Set `false` only on a route whose OpenAPI contract declares no 2xx body
   * (every one of them answers `204`). A 2xx that arrives empty on any other
   * route is a broken server and must reach `isError` — see `emptyBody.ts`.
   */
  expectBody?: boolean;
}

async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: CallOptions = {},
): Promise<{ response: Response; data: T | null; error?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (result.data as any) ?? null;
  // A bodyless 2xx where the contract declares a body arrives as `null` and
  // every `return res.data as T` below would ship it to a screen. Guard the
  // one funnel rather than each component — see `emptyBody.ts`.
  if (result.response?.ok)
    assertResponseBody(result.response, result.data, opts.expectBody !== false);
  // W39.79. `error` is openapi-fetch's **already-parsed** body for a non-2xx.
  // Without it `apiError` can only try to re-read a body the client consumed.
  return { response: result.response, data, error: (result as { error?: unknown }).error };
}

/**
 * Build an `ApiError` whose message is something a user can read.
 *
 * W39.53 — both non-2xx branches here passed `res.response.statusText`
 * positionally, which the copy guard could not see. Same helper, same reason as
 * in `album.ts` / `diary.ts`.
 */
async function apiError(
  res: { response: Response; data: unknown; error?: unknown },
  fallbackCode: string,
): Promise<ApiError> {
  // W39.81 — read openapi-fetch's already-parsed body first. The `Response` in
  // `res.response` has had its body consumed by the client
  // (`dist/index.js:244`), so `clone().json()` cannot read it and every media
  // error fell through to the fallback. See `album.ts` for the full account,
  // including why W39.79's "repo-wide" claim was five modules short.
  let body: unknown = res.error ?? null;
  if (body === null) {
    try {
      body = await res.response.clone().json();
    } catch {
      body = null;
    }
  }
  const err = (
    body as {
      error?: { code?: string; message?: string; details?: unknown };
    } | null
  )?.error;
  return new ApiError(
    res.response.status,
    err?.code ?? fallbackCode,
    err?.message ?? API_REQUEST_FAILED,
  );
}

export interface MediaOut {
  id: string;
  /** ``./uploads/{category}/{YYYY}/{MM}/{uuid}.{ext}`` (forward slashes). */
  file_path: string;
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  uploaded_by: string;
  created_at: string;
}

/**
 * POST /api/v1/media — multipart upload (image only).
 *
 * @param file  Browser ``File`` handle (image/* with max 8MB hard cap per
 *               backend; backend enforces MIME whitelist).
 * @returns Media row including the assigned id for diary attachment.
 */
export async function uploadMedia(file: File): Promise<MediaOut> {
  const form = new FormData();
  form.append("file", file);
  // openapi-fetch forbids setting Content-Type with FormData — it computes
  // the multipart boundary itself. Cast to any to bypass the strict typing.
  const opts = {
    body: form,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headers: { "Content-Type": undefined as any },
  };
  const res = await call<MediaOut>("POST", "/api/v1/media", opts);
  if (!res.response.ok) throw await apiError(res, "MEDIA_UPLOAD_FAILED");
  return res.data as MediaOut;
}

/** DELETE /api/v1/media/{id} — owner-only soft-delete. */
export async function deleteMedia(mediaId: string): Promise<void> {
  const res = await call<unknown>("DELETE", `/api/v1/media/${mediaId}`, { expectBody: false });
  if (!res.response.ok) throw await apiError(res, "MEDIA_DELETE_FAILED");
}

/** URL the ``<img src>`` uses — backend streams the bytes from
 * ``GET /api/v1/media/{id}/file`` (pair-member gated, per w3.md §5.1). */
export function mediaFileUrl(mediaId: string): string {
  return `/api/v1/media/${mediaId}/file`;
}
